import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { distinct } from "@lyfegen-platform/utils";
import { noop } from "lodash";
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
	FilterListStateType,
	FilterStateType,
	ListFilterState,
	TableFilterAction,
	useDocumentPagePersistedFilters,
} from "../../../../components/filters/models";
import { BaseAgreement, DocumentStatus, DocumentType } from "../../../../models/generated/graphql";
import { documentPageFilterFamily } from "../../../../store/documents";
import { userPersonalInfoState } from "../../../../store/user";
import { DocumentMessageModel, DocumentMessagingAction } from "../messaging/models";
import { InvoiceAcceptanceStatus } from "./decisions/AcceptanceStatus";
import { SearchFunction } from "./decisions/DocumentSearchFilter";
import { InvoiceStatusState } from "./UpdateInvoice";
import { getDocumentPageFiltersRecoilKeyword } from "./utils/filter-document-page";
import { DocumentRefundGroup, getRefundGroupInfo, mapRefundsToState } from "./utils/map-values";

type DocumentContextProps = {
	invoiceId?: string;
	documentType?: DocumentType;
	documentRefundGroups: Array<DocumentRefundGroup>;
	messages: Array<DocumentMessageModel>;
	messagingDispatcher: (action: DocumentMessagingAction) => void;
	selectedRefundIds: Array<string>;
	setSelectedRefundIds: (value: Array<string>) => void;
	status?: DocumentStatus | null;
	isDraft?: boolean;
	initialInvoiceState: Array<InvoiceStatusState>;
	isSummaryView: boolean;
	setIsSummaryView: (flag: boolean) => void;
	setIsSaving: Dispatch<SetStateAction<boolean>>;
	refetch: () => void;
	baseAgreements: Array<BaseAgreement>;
	uploadedVariantKey: string | null | undefined;
	uploadedOn: string | null | undefined;
	dunningDate: string | null | undefined;
};

type StateChange = { fdpUuid: string; status: InvoiceAcceptanceStatus };

type DocumentContextState = {
	invoiceId?: string;
	documentType?: DocumentType;
	isCaseless: boolean;
	currentFilters: FilterListStateType;
	baseAgreements: Array<BaseAgreement>;
	messages: Array<DocumentMessageModel>;
	messagingDispatcher: (action: DocumentMessagingAction) => void;
	dispatchCurrentFilters: Dispatch<TableFilterAction>;
	setIsSaving: Dispatch<SetStateAction<boolean>>;
	handleSearch: SearchFunction;
	filteredData: Array<DocumentRefundGroup>;
	status?: DocumentStatus | null;
	recoilKeyword: string;
	initialInvoiceState: Array<InvoiceStatusState>;
	currentState: Array<InvoiceStatusState>;
	onStateChange: (changes: Array<StateChange>) => void;
	isSummaryView: boolean;
	setIsSummaryView: (state: boolean) => void;
	dateAttributeNames: Array<string>;
	refetch: () => void;
	selectedRefundIds: Array<string>;
	setSelectedRefundIds: (ids: Array<string>) => void;
	uploadedInfo?: { uploadedVariantKey: string; uploadedOn: string };
	setUploaded: (info: { uploadedVariantKey: string; uploadedOn: string }) => void;
	dunningDate: string | null | undefined;
};

const defaultDocumentContextState: DocumentContextState = {
	isCaseless: false,
	currentFilters: [],
	baseAgreements: [],
	messages: [],
	messagingDispatcher: noop,
	dispatchCurrentFilters: noop,
	setIsSaving: noop,
	handleSearch: noop,
	filteredData: [],
	status: null,
	recoilKeyword: "",
	initialInvoiceState: [],
	currentState: [],
	dateAttributeNames: [],
	isSummaryView: false,
	selectedRefundIds: [],
	dunningDate: undefined,
	setIsSummaryView: noop,
	onStateChange: noop,
	refetch: noop,
	setSelectedRefundIds: noop,
	setUploaded: noop,
};

export const DocumentContext = createContext<DocumentContextState>(defaultDocumentContextState);

export const useDocumentContext = () => useContext(DocumentContext);

export default ({
	invoiceId,
	messages,
	messagingDispatcher,
	selectedRefundIds,
	setSelectedRefundIds,
	baseAgreements,
	documentRefundGroups,
	children,
	documentType,
	isDraft = false,
	uploadedVariantKey,
	uploadedOn,
	...rest
}: PropsWithChildren<DocumentContextProps>) => {
	const {
		organization: { externalId: tenantId },
		transactionsInRefundInvoiceDocumentTableColumns,
		transactionsInCreditNoteDocumentTableColumns,
		transactionsInFreeOfChargeDocumentTableColumns,
	} = useRecoilValue(userPersonalInfoState);
	const recoilKeyword = getDocumentPageFiltersRecoilKeyword({ tenantId, invoiceId });
	const setFilterPersistedState = useSetRecoilState(documentPageFilterFamily(recoilKeyword));
	const isCaseless = useMemo(
		() => documentRefundGroups.every((group) => getRefundGroupInfo(group, baseAgreements).isCaseless),
		[documentRefundGroups]
	);

	const dateAttributeNames = distinct(
		documentRefundGroups
			.flatMap((g) =>
				g.refunds.flatMap((r) => r.documentFinancialDataPointConnection.edges.flatMap((e) => e.node.evaluates.dateAttributeName))
			)
			.filter((name): name is string => Boolean(name))
	);

	const { handleSearch, currentFilters, dispatchCurrentFilters, filteredData } = useDocumentPagePersistedFilters(
		documentRefundGroups,
		baseAgreements,
		recoilKeyword,
		isCaseless,
		isDraft,
		dateAttributeNames,
		messages
	);

	const getRefundColumnNames = () => {
		switch (documentType) {
			case DocumentType.Invoice:
				return transactionsInRefundInvoiceDocumentTableColumns;

			case DocumentType.CreditNote:
				return transactionsInCreditNoteDocumentTableColumns;

			case DocumentType.FreeOfCharge:
				return transactionsInFreeOfChargeDocumentTableColumns;

			default:
				return [];
		}
	};

	const filteredFilters = useMemo(
		() =>
			currentFilters.filter(
				(filter) =>
					filter.selectionType !== "list" ||
					((filter as ListFilterState).options.length &&
						(filter.field !== AttributeIdentifiers.PurchaseId ||
							getRefundColumnNames().includes(AttributeIdentifiers.PurchaseId)))
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			currentFilters,
			transactionsInRefundInvoiceDocumentTableColumns?.join(),
			transactionsInCreditNoteDocumentTableColumns?.join(),
			transactionsInFreeOfChargeDocumentTableColumns?.join(),
		]
	);

	const [currentState, setCurrentState] = useState(documentRefundGroups.flatMap(mapRefundsToState));

	const onStateChange = useCallback(
		(changes: Array<StateChange>) => {
			setCurrentState(
				currentState.map((transactionState) => {
					const change = changes.find(({ fdpUuid }) => fdpUuid === transactionState.fdpUuid);

					return change ? { ...transactionState, status: change.status } : transactionState;
				})
			);
		},
		[currentState]
	);

	const [uploadedInfo, setUploaded] = useState(uploadedVariantKey && uploadedOn ? { uploadedVariantKey, uploadedOn } : undefined);

	useEffect(() => {
		setFilterPersistedState(
			currentFilters.map(
				(f): FilterStateType =>
					f.selectionType === "list" && filteredFilters.some((ff) => ff.field === f.field)
						? f
						: ({
								...f,
								isEmpty: true,
								selectedValueCount: 0,
								options: (f as ListFilterState).options?.map((o) => ({
									...o,
									isSelected: false,
								})),
						  } as ListFilterState)
			)
		);
	}, [currentFilters, filteredFilters, setFilterPersistedState]);

	useEffect(() => {
		setCurrentState(documentRefundGroups.flatMap(mapRefundsToState));
	}, [rest.initialInvoiceState, documentRefundGroups]);

	return (
		<DocumentContext.Provider
			value={{
				recoilKeyword,
				invoiceId,
				messages,
				messagingDispatcher,
				documentType,
				isCaseless,
				baseAgreements,
				currentFilters: filteredFilters,
				dispatchCurrentFilters,
				handleSearch,
				filteredData,
				currentState,
				dateAttributeNames,
				onStateChange,
				selectedRefundIds,
				setSelectedRefundIds,
				uploadedInfo,
				setUploaded,
				...rest,
			}}
		>
			{children}
		</DocumentContext.Provider>
	);
};
